import type { CachedImageRecord, ImageUrlFields } from './types'

const DB_NAME = 'digen-image-cache'
const STORE_NAME = 'images'
const DB_VERSION = 1
const MAX_ENTRIES = 300
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

const blobUrlCache = new Map<string, string>()

function openDatabase(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB is not available'))
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onerror = () => reject(request.error ?? new Error('Failed to open image cache'))
    request.onsuccess = () => resolve(request.result)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' })
      }
    }
  })
}

async function withStore<T>(
  mode: IDBTransactionMode,
  runner: (store: IDBObjectStore) => IDBRequest<T> | Promise<T>
): Promise<T> {
  const db = await openDatabase()
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, mode)
    const store = transaction.objectStore(STORE_NAME)

    Promise.resolve(runner(store))
      .then(result => {
        if (result instanceof IDBRequest) {
          result.onsuccess = () => resolve(result.result as T)
          result.onerror = () => reject(result.error ?? new Error('IndexedDB request failed'))
          return
        }
        resolve(result)
      })
      .catch(reject)

    transaction.oncomplete = () => db.close()
    transaction.onerror = () => {
      db.close()
      reject(transaction.error ?? new Error('IndexedDB transaction failed'))
    }
  })
}

async function pruneExpiredAndOverflow(): Promise<void> {
  const db = await openDatabase()
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite')
    const store = transaction.objectStore(STORE_NAME)
    const request = store.getAll()

    request.onsuccess = () => {
      const records = (request.result as CachedImageRecord[]).sort(
        (a, b) => b.createdAt - a.createdAt
      )
      const now = Date.now()
      const staleKeys = records
        .filter(record => now - record.createdAt > MAX_AGE_MS)
        .map(record => record.key)
      const overflowKeys =
        records.length > MAX_ENTRIES
          ? records.slice(MAX_ENTRIES).map(record => record.key)
          : []
      const keysToDelete = new Set([...staleKeys, ...overflowKeys])

      keysToDelete.forEach(key => {
        revokeBlobUrl(key)
        store.delete(key)
      })
    }

    request.onerror = () => reject(request.error ?? new Error('Failed to prune image cache'))
    transaction.oncomplete = () => {
      db.close()
      resolve()
    }
    transaction.onerror = () => {
      db.close()
      reject(transaction.error ?? new Error('Failed to prune image cache'))
    }
  })
}

function revokeBlobUrl(cacheKey: string): void {
  const existing = blobUrlCache.get(cacheKey)
  if (existing) {
    URL.revokeObjectURL(existing)
    blobUrlCache.delete(cacheKey)
  }
}

export async function saveImageToCache(
  cacheKey: string,
  blob: Blob,
  metadata: ImageUrlFields
): Promise<void> {
  if (typeof indexedDB === 'undefined') return

  const record: CachedImageRecord = {
    key: cacheKey,
    blob,
    createdAt: Date.now(),
    metadata
  }

  await withStore('readwrite', store => store.put(record))
  revokeBlobUrl(cacheKey)
  await pruneExpiredAndOverflow()
}

export async function getCachedImageBlobUrl(cacheKey: string): Promise<string | null> {
  if (!cacheKey || typeof indexedDB === 'undefined') return null

  const cached = blobUrlCache.get(cacheKey)
  if (cached) return cached

  const record = await withStore<CachedImageRecord | undefined>('readonly', store =>
    store.get(cacheKey)
  )

  if (!record?.blob) {
    revokeBlobUrl(cacheKey)
    return null
  }

  const blobUrl = URL.createObjectURL(record.blob)
  blobUrlCache.set(cacheKey, blobUrl)
  return blobUrl
}

export async function hasCachedImage(cacheKey: string): Promise<boolean> {
  if (!cacheKey || typeof indexedDB === 'undefined') return false

  try {
    const record = await withStore<CachedImageRecord | undefined>('readonly', store =>
      store.get(cacheKey)
    )
    return !!record?.blob
  } catch {
    return false
  }
}

export async function deleteCachedImage(cacheKey: string): Promise<void> {
  if (!cacheKey || typeof indexedDB === 'undefined') return
  revokeBlobUrl(cacheKey)
  await withStore('readwrite', store => store.delete(cacheKey))
}

export function createImageCacheKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `img-cache-${Date.now()}-${Math.random().toString(36).slice(2)}`
}
